// lib/upgradeNotice.js  (BL-053 Story 53.1 — Account Upgrade-Notice Correction)
//
// The Stripe Checkout `success_url` sends the customer back to /account with
// `?upgraded=true`. Per ADR-072 §4 that marker is an UNTRUSTED INTENT SIGNAL —
// the browser is not the entitlement authority, so the marker is never proof of
// payment, of a completed Checkout, or of an entitlement change. It says only
// "this navigation came from a checkout attempt."
//
// Whether an upgrade actually happened is decided exclusively by the
// authoritative profile projection (`subscription_status` + canonical
// `subscription_tier`), which the server writes from reconciled Stripe state.
// Every unknown, missing, or malformed shape fails closed.
//
// Pure module — no React, no routing, no network. The Account page owns the
// wiring (wait for load → evaluate once → consume the marker with replace
// semantics); this module owns the decision so it can be exhaustively tested.

import { CANONICAL_TIERS } from "./entitlements";

// The Checkout redirect marker.
export const UPGRADED_PARAM = "upgraded";

// Only `active` grants paid entitlement. Every other Stripe status —
// trialing, canceled, incomplete, incomplete_expired, past_due, unpaid,
// paused — is non-paying for the purposes of an upgrade-success notice
// (ADR-072 §1 lifecycle state contract).
const ACTIVE_STATUS = "active";

// Canonical PAID tiers, derived from the single canonical tier catalog
// (lib/entitlements.js) so a tier rename cannot drift and the retired `pro`
// label can never reappear. `free` is canonical but grants nothing.
export const PAID_TIERS = Object.freeze(
  CANONICAL_TIERS.filter((tier) => tier !== "free")
);

function readParam(search) {
  if (typeof search !== "string") return null;
  return new URLSearchParams(search).get(UPGRADED_PARAM);
}

// True when the marker is present at all, whatever its value. Presence — not
// truthiness — is what makes it consumable, so a stray `?upgraded=0` is not
// left behind in the address bar.
export function hasUpgradeParam(search) {
  return readParam(search) !== null;
}

// True only for the exact intent signal Checkout emits. Anything else is not
// an upgrade intent and must never open the success path.
export function hasUpgradeIntent(search) {
  return readParam(search) === "true";
}

// The authoritative test: an upgrade is confirmed only by an `active`
// subscription on a canonical paid tier. Fails closed on anything else.
export function isUpgradeConfirmed(state) {
  if (!state || typeof state !== "object") return false;
  if (state.status !== ACTIVE_STATUS) return false;
  return PAID_TIERS.includes(state.tier);
}

// Remove the marker from a location search string, preserving every unrelated
// parameter. Returns a leading-`?` search string, or '' when nothing is left.
export function stripUpgradedParam(search) {
  if (typeof search !== "string" || search === "") return "";
  const params = new URLSearchParams(search);
  params.delete(UPGRADED_PARAM);
  const rest = params.toString();
  return rest ? `?${rest}` : "";
}
